import { ComponentId, FormId, FormShortId, PageId } from "../types/ids";
import * as t from "io-ts";
import { literalUnion, forInterface } from "../io-ts/extensions";
import { FormHandle } from "../types/form/handle";
import { PageType } from "../types/form/page-type";
import { ComponentType } from "../types/form/component-type";
import { ComponentValidations } from "../types/form/component-validations";
import { ComponentValueType } from "../types/form/component-value-type";
import { Score } from "../types/form/submission/score";
import { FormProperties } from "../types/form/form-properties";
import { UnresolvedStaticPath } from "../types/static-path";



export const Theme = t.type({
  styles: t.string,
});
export type Theme = t.TypeOf<typeof Theme>;

export interface PageBase {
  id: PageId;
  type: PageType;
  title: string;
  heading: string;
  description: string;
  encrypt: boolean | undefined;
  addToScore: boolean | undefined;
  validations: {
    required: boolean | string | undefined;
  };
  components: Component[];
  notes: string[] | undefined;
}
const PageBase = t.type({
  id: PageId,
  title: t.string,
  heading: t.string,
  description: t.string,
  encrypt: t.union([t.undefined, t.boolean]),
  addToScore: t.union([t.undefined, t.boolean]),
  validations: t.type({
    required: t.union([t.undefined, t.boolean, t.string])
  }),
  notes: t.union([t.undefined, t.array(t.string)]),
});

export interface ComponentBase {
  id: ComponentId;
  componentType: ComponentType | undefined;
  index: number;
  heading: string;
  label: string;
  note: string | undefined;
  value: Score | undefined;
  condition: string | undefined;
}
const ComponentBase = t.type({
  id: ComponentId,
  index: t.number,
  heading: t.string,
  label: t.string,
  condition: t.union([t.string, t.undefined]),
});

export interface ComponentOption extends ComponentBase {
  componentType: "multiple-choice";
  index: number;
  heading: string;
  description: string;
  icon: UnresolvedStaticPath;
  iconLabel: string;
  label: string;
  note: string;
  cta: string;
  primary: boolean | undefined;
  value: Score;
  list: {
    id: string;
    icon: UnresolvedStaticPath;
    label: string;
  }[];
}
const ComponentOption = forInterface<ComponentOption>()(
  t.intersection([
    ComponentBase,
    t.type({
      componentType: literalUnion<ComponentType>()("multiple-choice"),
      index: t.number,
      heading: t.string,
      description: t.string,
      icon: UnresolvedStaticPath,
      iconLabel: t.string,
      label: t.string,
      note: t.string,
      cta: t.string,
      primary: t.union([t.undefined, t.boolean]),
      value: Score,
      list: t.array(t.type({
        id: t.string,
        icon: UnresolvedStaticPath,
        label: t.string,
      }))
    })]),
  x => x);

export interface PageOption extends PageBase {
  type: "list_single_choice";
  components: ComponentOption[];
}
const PageOption = forInterface<PageOption>()(
  t.intersection([
    PageBase,
    t.type({
      type: literalUnion<PageType>()("list_single_choice"),
      components: t.array(ComponentOption),
    })
  ]),
  x => x);

export interface ComponentIconOption extends ComponentBase {
  componentType: "multiple-choice";
  description: string;
  icon: UnresolvedStaticPath;
  disabled: boolean;
  validations: ComponentValidations;
  list: {
    id: string;
    icon: UnresolvedStaticPath;
    label: string;
  }[];
}
const ComponentIconOption = forInterface<ComponentIconOption>()(
  t.intersection([
    ComponentBase,
    t.type({
      note: t.union([t.undefined, t.string]),
      value: t.union([t.undefined, Score]),

      componentType: literalUnion<ComponentType>()("multiple-choice"),
      description: t.string,
      icon: UnresolvedStaticPath,
      disabled: t.boolean,
      validations: ComponentValidations,
      list: t.array(t.intersection([
        t.type({
          id: t.string,
          icon: UnresolvedStaticPath,
          label: t.string,
        }),
        t.partial({
          ref: t.union([t.undefined, PageId]),
        })])),
    })]),
  x => x);

export interface PageIconOption extends PageBase {
  type: "icon_multiple_choice";
  components: ComponentIconOption[];
  properties: {
    allowMultipleSelection: boolean;
    randomize: boolean;
    verticalAlignment: boolean;
  };
}
const PageIconOption = forInterface<PageIconOption>()(
  t.intersection([
    PageBase,
    t.type({
      type: literalUnion<PageType>()("icon_multiple_choice"),
      components: t.array(ComponentIconOption),
      properties: t.type({
        allowMultipleSelection: t.boolean,
        randomize: t.boolean,
        verticalAlignment: t.boolean,
      }),
    })]),
  x => x);

export interface ComponentTextOption extends ComponentBase {
  componentType: "multiple-choice";
  validations: ComponentValidations | undefined;
}
const ComponentTextOption = forInterface<ComponentTextOption>()(
  t.intersection([
    ComponentBase,
    t.type({
      note: t.union([t.undefined, t.string]),
      value: t.union([t.undefined, Score]),

      componentType: literalUnion<ComponentType>()("multiple-choice"),
      validations: t.union([t.undefined, ComponentValidations]),
    })]),
  x => x);

export interface PageTextOption extends PageBase {
  type: "text_multiple_choice";
  components: ComponentTextOption[];
  properties: {
    allowMultipleSelection: boolean;
    randomize: boolean;
    verticalAlignment: boolean;
  };
}
const PageTextOption = forInterface<PageTextOption>()(
  t.intersection([
    PageBase,
    t.type({
      type: literalUnion<PageType>()("text_multiple_choice"),
      components: t.array(ComponentTextOption),
      properties: t.type({
        allowMultipleSelection: t.boolean,
        randomize: t.boolean,
        verticalAlignment: t.boolean,
      }),
    })]),
  x => x);

export interface ComponentInputBase extends ComponentBase {
  componentType: "input";
  type: ComponentValueType;

  label: string;
  validations: ComponentValidations | undefined;
}
const ComponentInputBase = t.intersection([
  ComponentBase,
  t.type({
    note: t.union([t.undefined, t.string]),
    value: t.union([t.undefined, Score]),

    componentType: literalUnion<ComponentType>()("input"),

    validations: t.union([t.undefined, ComponentValidations]),
  })]);

export const ComponentInputCommonValueType = t.keyof({
  "text": null,
  "email": null,
  "number": null,
  "checkbox": null,
  "time": null,
  "file": null,
  "date": null,
  "signature": null,
});
export type ComponentInputCommonValueType = t.TypeOf<typeof ComponentInputCommonValueType>;

export interface ComponentInputCommon extends ComponentInputBase {
  type: ComponentInputCommonValueType,
  icon: UnresolvedStaticPath,
  placeholder: string,
  showLabel: boolean,
  required: boolean,
}

const ComponentInputCommon = forInterface<ComponentInputCommon>()(
  t.intersection([
    ComponentInputBase,
    t.type({
      type: ComponentInputCommonValueType,
      icon: UnresolvedStaticPath,
      placeholder: t.string,
      showLabel: t.boolean,
      required: t.boolean,
    })]),
  x => x);

export interface ComponentInputRadioOption {
  label: string;
  value: number | string;
}
const ComponentInputRadioOption = t.type({
  label: t.string,
  value: t.union([t.number, t.string]),
})

export interface ComponentInputRadio extends ComponentInputBase {
  type: "radio",
  properties: {
    options: ComponentInputRadioOption[],
  }
}
const ComponentInputRadio = forInterface<ComponentInputRadio>()(
  t.intersection([
    ComponentInputBase,
    t.type({
      type: literalUnion<ComponentValueType>()("radio"),
      properties: t.type({
        options: t.array(ComponentInputRadioOption),
      }),
    })]),
  x => x);

export type ComponentInput = ComponentInputCommon | ComponentInputRadio;
const ComponentInput = forInterface<ComponentInput>()(
  t.union([ComponentInputCommon, ComponentInputRadio]),
  x => x
);

export interface PageInput extends PageBase {
  type: "input";
  components: Array<ComponentInput>;
  properties: {
    allowMultipleSelection: boolean;
    randomize: boolean;
    verticalAlignment: boolean;
  };
}
const PageInput = forInterface<PageInput>()(
  t.intersection([
    PageBase,
    t.type({
      type: literalUnion<PageType>()("input"),
      components: t.array(ComponentInput),
      properties: t.type({
        allowMultipleSelection: t.boolean,
        randomize: t.boolean,
        verticalAlignment: t.boolean,
      }),
    })]),
  x => x);

export interface ComponentOptInLink {
  label: string;
  link: string;
}
const ComponentOptInLink = forInterface<ComponentOptInLink>()(
  t.type({
    label: t.string,
    link: t.string,
  }),
  x => x);

export interface ComponentOptInBase extends ComponentBase {
  componentType: "input" | "summary",
  label: string;
  links: ComponentOptInLink[];
  validations: {
    required: boolean | string | undefined,
  },
}
const ComponentOptInBase = t.intersection([
  ComponentBase,
  t.type({
    note: t.union([t.undefined, t.string]),
    value: t.union([t.undefined, Score]),

    validations: t.type({
      required: t.union([t.undefined, t.boolean, t.string]),
    }),
    links: t.array(ComponentOptInLink),
  })]);

export const ComponentOptInInputValueType = t.keyof({
  "checkbox": null,
  "signature": null,
});
export type ComponentOptInInputValueType = t.TypeOf<typeof ComponentOptInInputValueType>;

export interface ComponentOptInInput extends ComponentOptInBase {
  componentType: "input";
  type: ComponentOptInInputValueType;
  label: string;
  showLabel: boolean;
  links: ComponentOptInLink[];
  validations: {
    required: boolean | string | undefined,
  },
}
const ComponentOptInInput = forInterface<ComponentOptInInput>()(
  t.intersection([
    ComponentOptInBase,
    t.type({
      componentType: literalUnion<ComponentType>()("input"),
      type: ComponentOptInInputValueType,
      showLabel: t.boolean,
      validations: t.type({
        required: t.union([t.undefined, t.boolean, t.string]),
      }),
      links: t.array(ComponentOptInLink),
    })]),
  x => x);

export interface ComponentOptInSummary extends ComponentOptInBase {
  componentType: "summary";
  label: string;
  links: ComponentOptInLink[];
  validations: {
    required: boolean | string | undefined,
  },
  icon: UnresolvedStaticPath;
  iconLabel: string;
  note: string;
  cta: string;
  list: { id: string; icon: UnresolvedStaticPath; label: string; ref?: PageId | undefined; }[];
}
const ComponentOptInSummary = forInterface<ComponentOptInSummary>()(
  t.intersection([
    ComponentOptInBase,
    t.type({
      componentType: literalUnion<ComponentType>()("summary"),
      validations: t.type({
        required: t.union([t.undefined, t.boolean, t.string]),
      }),
      links: t.array(ComponentOptInLink),
      icon: UnresolvedStaticPath,
      iconLabel: t.string,
      note: t.string,
      cta: t.string,
      list: t.array(t.intersection([
        t.type({
          id: t.string,
          icon: UnresolvedStaticPath,
          label: t.string,
        }),
        t.partial({
          ref: t.union([t.undefined, PageId]),
        })])),
    })]),
  x => x);

export type ComponentOptIn = ComponentOptInInput | ComponentOptInSummary;
const ComponentOptIn = forInterface<ComponentOptIn>()(
  t.union([ComponentOptInInput, ComponentOptInSummary]),
  x => x);

export interface PageOptIn extends PageBase {
  type: "opt-in";
  components: Array<ComponentOptIn>;
}
const PageOptIn = forInterface<PageOptIn>()(
  t.intersection([
    PageBase,
    t.type({
      type: literalUnion<PageType>()("opt-in"),
      components: t.array(ComponentOptIn),
    })]),
  x => x);

type Component = ComponentBase
  & (ComponentOption | ComponentIconOption | ComponentTextOption | ComponentInput | ComponentOptIn);

export type Page = PageOption | PageIconOption | PageTextOption | PageInput | PageOptIn;
export const Page = forInterface<Page>()(
  t.union([PageOption, PageIconOption, PageTextOption, PageInput, PageOptIn]),
  x => x);

export const ThankYouPageData = t.type({
  icon: UnresolvedStaticPath,
  heading: t.string,
  description: t.string,
});
export type ThankYouPageData = t.TypeOf<typeof ThankYouPageData>;

export const FormData = t.type({
  id: FormId,
  handle: FormHandle,
  shortId: FormShortId,
  sortedPages: t.array(PageId),
  scoreExpression: t.union([t.null, t.string]),
  thankYouPage: t.union([t.null, ThankYouPageData]),
  properties: FormProperties,
  pages: t.array(Page),
  theme: t.union([t.null, Theme]),
});
export type FormData = t.TypeOf<typeof FormData>;
